import { chatboxDirectionSchema, type Settings } from "@go-novus/admin-api";
import {
  Panel,
  SegmentedControl,
  SourceRow,
  Switch,
  TextField,
} from "@go-novus/ui";

import * as m from "#/paraglide/messages";

import type { PreviewControl } from "../../hooks/use-preview-control";
import type { DraftErrors } from "../../lib/settings-draft";
import { PreviewLayout, type DraftView } from "../preview";
import { LogoField } from "./logo-field";
import { SuggestedQuestionsField } from "./suggested-questions-field";

type Chatbox = Settings["chatbox"];

const DIRECTION_LABEL: Record<Chatbox["direction"], () => string> = {
  hairline: m.settings_direction_hairline,
  editorial: m.settings_direction_editorial,
};

const WELCOME_MAX_LENGTH = 80;

export interface ChatWindowSectionProps {
  draftView: DraftView;
  errors: DraftErrors;
  onAgentNameChange: (name: string) => void;
  onChange: (next: Chatbox) => void;
  onLogoChange: (file: File | null) => void;
  preview: PreviewControl;
  /** 保存进行中，Logo 的按钮跟着锁上。 */
  saving: boolean;
}

/** Chat window 标签：标题栏、欢迎页和聊天窗样式，右边一张预览。 */
export function ChatWindowSection({
  draftView,
  errors,
  onAgentNameChange,
  onChange,
  onLogoChange,
  preview,
  saving,
}: ChatWindowSectionProps) {
  const { agentName, chatbox, logoUrl } = draftView;

  function patch(next: Partial<Chatbox>): void {
    onChange({ ...chatbox, ...next });
  }

  return (
    <PreviewLayout draftView={draftView} preview={preview}>
      <Panel>
        <h2 className="px-4.5 pt-4.5 text-headline font-bold text-[var(--content-fg-strong)]">
          {m.settings_title_bar_heading()}
        </h2>
        <div className="p-5">
          <TextField
            label={m.settings_agent_name_label()}
            hint={m.settings_agent_copy()}
            error={errors.agentName}
            value={agentName}
            onChange={(event) => {
              onAgentNameChange(event.target.value);
            }}
          />
        </div>
        <LogoField
          disabled={saving}
          logoUrl={logoUrl}
          onChange={onLogoChange}
        />
      </Panel>
      <Panel>
        <h2 className="px-4.5 pt-4.5 text-headline font-bold text-[var(--content-fg-strong)]">
          {m.settings_welcome_heading()}
        </h2>
        <div className="p-5">
          <TextField
            error={errors.welcomeMessage}
            label={m.settings_welcome_label()}
            onChange={(event) => {
              patch({
                welcomeMessage: event.target.value,
              });
            }}
            value={chatbox.welcomeMessage}
          />
          <div className="mt-1.25 flex justify-between gap-3 text-micro text-[var(--content-fg-subtle)]">
            <span>{m.settings_welcome_hint()}</span>
            <span>
              {m.settings_welcome_count({
                count: chatbox.welcomeMessage.length,
                max: WELCOME_MAX_LENGTH,
              })}
            </span>
          </div>
        </div>
        <SuggestedQuestionsField
          errors={errors.suggestedQuestions}
          onChange={(suggestedQuestions) => {
            patch({ suggestedQuestions });
          }}
          questions={chatbox.suggestedQuestions}
        />
      </Panel>
      <Panel>
        <h2 className="px-4.5 pt-4.5 text-headline font-bold text-[var(--content-fg-strong)]">
          {m.settings_messages_heading()}
        </h2>
        <div className="p-4.5">
          <SegmentedControl
            hint={m.settings_direction_hint()}
            label={m.settings_direction_label()}
            onChange={(value) => {
              const direction = chatboxDirectionSchema.safeParse(value);

              if (direction.success) {
                patch({ direction: direction.data });
              }
            }}
            options={chatboxDirectionSchema.options.map((value) => ({
              label: DIRECTION_LABEL[value](),
              value,
            }))}
            value={chatbox.direction}
          />
        </div>
        <div className="border-t border-[var(--border-surface-default)]">
          <SourceRow
            action={
              <Switch
                checked={chatbox.replyCopyButton}
                label={m.settings_copy_button_title()}
                onChange={(replyCopyButton) => {
                  patch({ replyCopyButton });
                }}
              />
            }
            copy={m.settings_copy_button_copy()}
            title={m.settings_copy_button_title()}
          />
        </div>
      </Panel>
    </PreviewLayout>
  );
}
